// StorySection.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, stagger, useAnimate } from "framer-motion";
import "../../styles/StorySection.css";

const stats = [
  { value: 80, suffix: "+", label: "Projects" },
  { value: 25, suffix: "+", label: "Customers" },
  { value: 2, suffix: "+", label: "Business Units" },
  { value: 4, suffix: "+", label: "Stores" },
];

// Count-up hook
function useCountUp(finalValue, start = 0, duration = 1400, inView = false) {
  const [val, setVal] = useState(start);
  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      setVal(Math.round(start + (finalValue - start) * p));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [finalValue, start, duration, inView]);
  return val;
}

// Child component so hooks are top-level
function StatCard({ value, suffix, label, index }) {
  const cardRef = useRef(null);
  const cardInView = useInView(cardRef, { once: true, margin: "0px 0px -10% 0px" });
  const count = useCountUp(value, 0, 1200 + index * 150, cardInView);

  return (
    <motion.article
      ref={cardRef}
      className="stat-card reveal"
      role="article"
      aria-label={`${value}${suffix} ${label}`}
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      whileHover={{ y: -6, rotateX: 1 }}
    >
      <div className="stat-value">
        {count}
        <span className="stat-suffix">{suffix}</span>
      </div>
      <div className="stat-label">{label}</div>
    </motion.article>
  );
}

const StorySection = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { amount: 0.3, once: true });
  const [scope, animate] = useAnimate();

  useEffect(() => {
    if (!inView) return;
    animate(
      ".reveal",
      { opacity: [0, 1], y: [14, 0] },
      { duration: 0.6, ease: "easeOut", delay: stagger(0.08) }
    );
  }, [inView, animate]);

  return (
    <section id="about" ref={sectionRef} className="story-section" style={{padding: "100px 20px" }} aria-labelledby="behind-brand-title">
      <div className="story-spotlight" aria-hidden="true" />
      <div className="story-container" ref={scope}>
        <header className="story-heading">
          <h2 id="behind-brand-title" className="reveal">Behind the Brand</h2>
          <p className="story-sub reveal">
            With over six years of experience in fine woodworking, we've built our
            reputation on exceptional craftsmanship, attention to detail, and unwavering commitment
            to quality.
          </p>
        </header>

        <div className="story-content">
          <div className="story-left reveal">
            <h3 className="story-left-title">Our Story</h3>
            <p>
              Founded in 2019, Icity Woodworks began as a passion project in a small garage
              workshop. Today, we’re proud to be one of the region’s premier custom woodworking
              specialists—serving discerning clients who appreciate the beauty and durability
              of handcrafted wood furniture.
            </p>
            <p>
              Every piece we create tells a story — from the careful selection of premium hardwoods
              to the final hand-rubbed finish. We believe that true craftsmanship never goes out of style.
            </p>
          </div>

          <aside className="story-right" aria-hidden="false">
            <div className="stats-grid">
              {stats.map((s, i) => (
                <StatCard key={i} value={s.value} suffix={s.suffix} label={s.label} index={i} />
              ))}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default StorySection;
